import React, { useState, useEffect } from "react";
import { FileSystemItem } from "types";
import { Folder, ChevronRight, Home } from "lucide-react";
import api from "api/axios";
import { Dialog } from "components/dialog";
import { useTranslation } from "hooks/useTranslation";

interface MoveItemsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onMove: (destinationFolderId: string | null) => void;
  itemsToMove: FileSystemItem[];
  sourceFolderId: string | null;
}

const MoveItemsDialog: React.FC<MoveItemsDialogProps> = ({
  isOpen,
  onClose,
  onMove,
  itemsToMove,
  sourceFolderId,
}) => {
  const [folders, setFolders] = useState<FileSystemItem[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<{ id: string; name: string }[]>(
    []
  );
  console.log("MoveItemsDialog", sourceFolderId);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [rootFolderId, setRootFolderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [effectiveSourceFolderId, setEffectiveSourceFolderId] = useState<string | null>(sourceFolderId);
  const { t } = useTranslation();

  const fetchFolders = async (folderId: string | null) => {
    setLoading(true);
    try {
      let fetchedItems: FileSystemItem[];
      if (folderId === null) {
        const { data } = await api.get<any[]>("/folders/");
        setRootFolderId(data[0]?.id || null);
        const { data: dataItem } = await api.get<FileSystemItem[]>(
          `/folders/${data[0].id}/documents`
        );
        fetchedItems = dataItem;
        // fetchedItems = data.map((item) => ({ ...item, type: "Folder" }));
      } else {
        const { data } = await api.get<FileSystemItem[]>(
          `/folders/${folderId}/documents`
        );
        fetchedItems = data;
      }
      const fetchedFolders = fetchedItems.filter(
        (item) => item.type === "Folder"
      );
      const fetchedFolderIds = fetchedFolders.map((folder) => folder.id);

      // Update the effective source folder ID based on whether it exists in current context
      if (!fetchedFolderIds.includes(sourceFolderId)) {
        setEffectiveSourceFolderId(null);
      } else {
        setEffectiveSourceFolderId(sourceFolderId);
      }

      const itemsToMoveIds = itemsToMove.map((item) => item.id);
      setFolders(
        fetchedFolders.filter((folder) => !itemsToMoveIds.includes(folder.id))
      );
    } catch (error) {
      console.error("Failed to fetch folders:", error);
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentFolderId(null);
      setBreadcrumb([]);
      setEffectiveSourceFolderId(sourceFolderId);
      fetchFolders(null);
    }
  }, [isOpen, sourceFolderId]);

  const handleFolderClick = (folder: FileSystemItem) => {
    setCurrentFolderId(folder.id);
    setBreadcrumb([...breadcrumb, { id: folder.id, name: folder.name }]);
    fetchFolders(folder.id);
  };

  const handleBreadcrumbClick = (folderId: string, index: number) => {
    const newFolderId = folderId === "root" ? null : folderId;
    setCurrentFolderId(newFolderId);
    setBreadcrumb(breadcrumb.slice(0, index + 1));
    fetchFolders(newFolderId);
  };

  const handleRootClick = () => {
    setCurrentFolderId(null);
    setBreadcrumb([]);
    fetchFolders(null);
  };

  const handleMove = () => {
    onMove(currentFolderId ?? rootFolderId);
  };

  return (
    <Dialog
      isOpen={isOpen}
      setIsOpen={(v) => {
        if (!v) {
          onClose();
        }
      }}
      className="w-full max-w-md"
      header={t("library.moveItems", { count: itemsToMove.length })}
    >
      <div className="my-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
        <Home
          className="mr-2 h-5 w-5 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
          onClick={handleRootClick}
        />
        <ChevronRight className="h-4 w-4" />
        {breadcrumb.map((crumb, index) => (
          <React.Fragment key={crumb.id}>
            <span
              className="cursor-pointer px-1 hover:underline"
              onClick={() =>
                handleBreadcrumbClick(
                  crumb.id === "root" ? "root" : crumb.id,
                  index
                )
              }
            >
              {crumb.name}
            </span>
            <ChevronRight className="h-4 w-4" />
          </React.Fragment>
        ))}
      </div>
      <div className="mt-4 max-h-60 overflow-y-auto">
        {loading ? (
          <p className="text-sm text-gray-500">{t("library.loading")}</p>
        ) : folders.length > 0 ? (
          folders.map((folder) => (
            <div
              key={folder.id}
              onClick={() => handleFolderClick(folder)}
              className="flex cursor-pointer items-center rounded-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Folder className="mr-3 h-5 w-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {folder.name}
              </span>
            </div>
          ))
        ) : (
          <p className="text-sm text-gray-500">{t("library.noSubFolders")}</p>
        )}
      </div>
      <div className="mt-6 flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          {t("common.cancel")}
        </button>
        <button
          onClick={handleMove}
          disabled={currentFolderId === (currentFolderId === null ? effectiveSourceFolderId : sourceFolderId) || loading}
          className="border-transparent inline-flex justify-center rounded-md border bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-blue-300"
        >
          {t("library.moveHere")}
        </button>
      </div>
    </Dialog>
  );
};

export default MoveItemsDialog;
